import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, DeviceMobile, Copy, Warning, Sparkle } from "@phosphor-icons/react";
import api from "../lib/api";
import { flag, fmtData, fmtEUR, fmtDate, countryName } from "../lib/format";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function CheckoutSuccess() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState("checking");
  const [order, setOrder] = useState(null);
  const [esim, setEsim] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data: s } = await api.get(`/checkout/status/${sessionId}`);
      if (s.payment_status !== "paid") {
        if (s.status === "expired") { setPhase("failed"); return; }
        setAttempts((a) => a + 1);
        return;
      }
      const { data } = await api.get(`/order/${s.order_id}`);
      setOrder(data.order); setEsim(data.esim);
      if (data.order.status === "fulfilled" && data.esim) setPhase("ready");
      else if (data.order.status === "provisioning_failed" && data.order.provisioning_attempts >= 3) setPhase("failed");
      else setPhase("provisioning");
    } catch {
      setAttempts((a) => a + 1);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setPhase("failed"); return; }
    poll();
    const id = setInterval(() => {
      setAttempts((a) => {
        if (a > 30) { clearInterval(id); return a; }
        poll();
        return a;
      });
    }, 2500);
    return () => clearInterval(id);
  }, [sessionId, poll]);

  const copy = () => {
    if (!esim?.qr_code_text) return;
    navigator.clipboard.writeText(esim.qr_code_text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen" data-testid="checkout-success-page">
      <Header />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
        {(phase === "checking" || phase === "provisioning") && (
          <div data-testid="checkout-provisioning">
            <div className="inline-flex items-center gap-2 pill bg-mint-soft text-mint mb-5">
              <CheckCircle size={16} weight="fill" /> {t("checkout.successTitle")}
            </div>
            <h1 className="font-display font-bold text-4xl md:text-6xl tracking-tight">{t("checkout.successSub")}</h1>
            <div className="mt-10 card-soft p-10 flex items-center gap-5">
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 rounded-full border-4 border-cobalt-100" />
                <div className="absolute inset-0 rounded-full border-4 border-cobalt-500 border-t-transparent animate-spin" />
              </div>
              <div>
                <div className="font-display font-semibold text-xl">{t("checkout.waiting")}</div>
                <div className="text-sm text-ink-500 mt-0.5">Usually 5–20 seconds</div>
              </div>
            </div>
          </div>
        )}

        {phase === "ready" && esim && (
          <div data-testid="esim-ready">
            <div className="inline-flex items-center gap-2 pill bg-mint-soft text-mint mb-5">
              <Sparkle size={14} weight="fill" /> {t("checkout.successTitle")}
            </div>
            <h1 className="font-display font-bold text-4xl md:text-6xl tracking-tight mb-10">{t("checkout.esimReady")}</h1>

            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-5">
              <div className="card-soft p-10 flex flex-col items-center bg-gradient-to-br from-white to-cobalt-50">
                <div className="overline mb-4">{t("esim.qrLabel")}</div>
                <div className="bg-white rounded-3xl p-6 shadow-soft">
                  <img src={`data:image/png;base64,${esim.qr_image_base64}`} alt="eSIM QR Code" className="w-64 h-64" data-testid="esim-qr-image" />
                </div>
                <p className="mt-6 text-sm text-ink-700 text-center max-w-md leading-relaxed">{t("checkout.scanQR")}</p>
                <div className="mt-6 flex gap-2 flex-wrap justify-center">
                  {esim.ios_install_link && (
                    <a href={esim.ios_install_link} target="_blank" rel="noreferrer" className="btn-primary text-sm !py-2.5 !px-4" data-testid="esim-ios-link">
                      <DeviceMobile size={16} weight="bold" /> {t("checkout.iosInstall")}
                    </a>
                  )}
                  <button onClick={copy} className="btn-ghost text-sm !py-2.5 !px-4" data-testid="esim-copy-lpa">
                    <Copy size={14} /> {copied ? t("checkout.copied") : t("checkout.copyLPA")}
                  </button>
                </div>
                <p className="mt-4 text-[11px] text-ink-400" data-testid="esim-provider-note">
                  {t("esim.providedBy", "eSIM connectivity provided by Yesim")}
                </p>
              </div>

              <div className="space-y-4">
                <div className="card-soft p-6">
                  <div className="overline mb-2">{t("checkout.planSummary")}</div>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{flag(esim.plan_snapshot.country_iso2)}</span>
                    <div>
                      <div className="font-display font-semibold text-lg tracking-tight">{esim.plan_snapshot.name}</div>
                      <div className="text-sm text-ink-500">{countryName(esim.plan_snapshot.country_iso2, i18n.language, esim.plan_snapshot.country)}</div>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="bg-cobalt-50 rounded-2xl p-3">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-cobalt-600">{t("common.data")}</div>
                      <div className="font-display font-bold mt-0.5">{fmtData(esim.data_total_mb)}</div>
                    </div>
                    <div className="bg-coral-soft/60 rounded-2xl p-3">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-coral-deep">{t("dash.expires")}</div>
                      <div className="font-display font-bold mt-0.5">{fmtDate(esim.expiry_date)}</div>
                    </div>
                  </div>
                </div>
                <div className="card-soft p-6">
                  <div className="overline mb-2">{t("checkout.iccid")}</div>
                  <div className="font-mono text-sm break-all text-ink-700">{esim.iccid}</div>
                </div>
                {order && (
                  <div className="card-soft p-6">
                    <div className="overline mb-2">{t("dash.orderId")}</div>
                    <div className="font-mono text-xs text-ink-500">{order.id}</div>
                    <div className="font-display font-bold text-2xl mt-2">{fmtEUR(order.amount)}</div>
                  </div>
                )}
                <Link to="/dashboard" className="btn-primary w-full" data-testid="go-to-dashboard">{t("common.dashboard")}</Link>
              </div>
            </div>
            <p className="mt-8 text-sm text-ink-500">{t("checkout.trouble")}</p>
          </div>
        )}

        {phase === "failed" && (
          <div className="card-soft p-10" data-testid="checkout-failed">
            <div className="inline-flex items-center gap-2 pill bg-red-50 text-destructive mb-4">
              <Warning size={14} weight="fill" /> {t("checkout.failedTitle")}
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tight mb-3">{t("checkout.failedTitle")}</h1>
            <p className="text-ink-700 leading-relaxed">{t("checkout.failedSub")}</p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => { setAttempts(0); setPhase("checking"); poll(); }} className="btn-outline" data-testid="checkout-retry">{t("common.retry")}</button>
              <Link to="/dashboard" className="btn-primary">{t("common.dashboard")}</Link>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
